  import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import Attachments from "../UserControls/Attachments/Attachments";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

/* ---------------------------
   🔹 PART LIST COMPONENT
---------------------------- */
function PartList({ onEdit }) {
  const [parts, setParts] = useState([]);
  const [filteredParts, setFilteredParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchParts = async () => {
      try {
        setLoading(true);
        const userName = sessionStorage.getItem("userName");
        const databaseKey = sessionStorage.getItem("databaseKey");
        const params = { userName, databaseKey };

        const response = await api.GetQcMasterList_API(params);
        const data =
          response?.data?.GetQcMasterList_API_APIjs?.Table ||
          response?.GetQcMasterList_API_APIjs?.Table ||
          [];

        setParts(data);
        setFilteredParts(data);
      } catch (error) {
        console.error("❌ Error fetching parts:", error);
        setParts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchParts();
  }, []);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    const filtered = parts.filter(
      (p) =>
        p.PartNo?.toLowerCase().includes(value) ||
        p.PartDesc?.toLowerCase().includes(value)
    );
    setFilteredParts(filtered);
    setCurrentPage(1);
  };

  /* ✅ EXPORT TO EXCEL */
  const handleExportExcel = () => {
    if (!filteredParts || filteredParts.length === 0) {
      alert("No data to export");
      return;
    }

    // Convert JSON to worksheet
    const ws = XLSX.utils.json_to_sheet(filteredParts);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Parts");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, `Parts_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const totalPages = Math.ceil(filteredParts.length / pageSize);
  const paginatedParts = filteredParts.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const nextPage = () =>
    currentPage < totalPages && setCurrentPage(currentPage + 1);
  const prevPage = () =>
    currentPage > 1 && setCurrentPage(currentPage - 1);

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-primary-600 text-lg font-medium animate-pulse">
          ⏳ Loading parts...
        </p>
      </div>
    );

  if (!parts.length)
    return (
      <div className="text-center text-neutral-500 py-20">
        <div className="text-6xl mb-4">📭</div>
        <p className="text-xl">No parts found in the system.</p>
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-full mx-auto bg-white rounded-lg shadow-lg">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-400 to-blue-500 text-white p-4 rounded-t-lg flex justify-between items-center">
          <h1 className="text-xl font-bold">Part List</h1>
          <button
            onClick={() => navigate("/dashboard")}
            className="bg-white/20 hover:bg-white/30 text-white px-3 py-2 rounded-lg font-semibold flex items-center space-x-1 transition-all duration-200"
            title="Back to Dashboard"
          >
            <span>⬅️</span>
            <span>Back</span>
          </button>
        </div>

        {/* Export Button in Header */}
        <div className="p-4 bg-gradient-to-r from-slate-50 to-slate-100 border-b-2 border-slate-200 flex justify-between items-center">
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search Part No / Description..."
            className="border border-slate-300 rounded px-3 py-2 text-sm focus:ring-1 focus:ring-blue-400 focus:border-blue-400 w-64"
          />
          <button
            onClick={handleExportExcel}
            className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white px-3 py-2 rounded-lg font-semibold flex items-center space-x-1 transition-all duration-200 shadow-md hover:shadow-lg text-sm"
            title="Export to Excel"
          >
            <span>📊</span>
            <span>Export</span>
          </button>
        </div>

        {/* Table */}
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-pulse text-xl text-slate-600 font-semibold">🔄 Loading parts...</div>
            </div>
          ) : (
            <div className="overflow-x-auto shadow-2xl rounded-2xl">
              <div className="max-h-[500px] overflow-y-auto">
                <table className="min-w-full bg-white border-collapse">
                  <thead className="bg-gradient-to-r from-slate-700 to-slate-800 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-bold text-white border-r border-slate-600">#</th>
                      <th className="px-4 py-3 text-left text-sm font-bold text-white border-r border-slate-600">Part No</th>
                      <th className="px-4 py-3 text-left text-sm font-bold text-white border-r border-slate-600">Description</th>
                      <th className="px-4 py-3 text-left text-sm font-bold text-white border-r border-slate-600">UOM</th>
                      <th className="px-4 py-3 text-left text-sm font-bold text-white border-r border-slate-600">Weight</th>
                      <th className="px-4 py-3 text-left text-sm font-bold text-white border-r border-slate-600">Material Type</th>
                      <th className="px-4 py-3 text-left text-sm font-bold text-white border-r border-slate-600">Action</th>
                      <th className="px-4 py-3 text-left text-sm font-bold text-white">Attachments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedParts.length > 0 ? (
                      paginatedParts.map((part, i) => (
                        <tr key={i} className={`transition-all duration-200 border-b border-slate-200 ${
                          i % 2 === 0
                            ? 'bg-white hover:bg-slate-100'
                            : 'bg-slate-50 hover:bg-slate-100'
                        }`}>
                          <td className="px-4 py-3 text-sm font-semibold text-slate-800 border-r border-slate-200">{(currentPage - 1) * pageSize + i + 1}</td>
                          <td className="px-4 py-3 text-sm text-slate-700 border-r border-slate-200 font-medium">{part.PartNo}</td>
                          <td className="px-4 py-3 text-sm text-slate-700 border-r border-slate-200">{part.PartDesc}</td>
                          <td className="px-4 py-3 text-sm text-slate-700 border-r border-slate-200">{part.Uom}</td>
                          <td className="px-4 py-3 text-sm text-slate-700 border-r border-slate-200">{part.Weight}</td>
                          <td className="px-4 py-3 text-sm text-slate-700 border-r border-slate-200">{part.MaterialType}</td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => onEdit(part)}
                              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-3 py-1 rounded-lg font-semibold flex items-center space-x-1 transition-all duration-200 shadow-sm hover:shadow-md"
                            >
                              <span>✏️</span>
                              <span>Edit</span>
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <Attachments
                              mdlId="Item_Master"
                              primeId={part.Itemid}
                              primeName={part.PartNo}
                              uploadedBy={
                                localStorage.getItem("username") || "UnknownUser"
                              }
                            />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="px-6 py-12 text-center text-slate-500 bg-slate-100">
                          <div className="flex flex-col items-center space-y-2">
                            <span className="text-4xl">📭</span>
                            <span>No matching records found</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination */}
          <div className="flex justify-between items-center mt-8 px-4">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-5 py-3 rounded-xl font-bold transition-all duration-200 flex items-center space-x-2 ${
                  currentPage === 1
                    ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                }`}
              >
                <span className="text-lg">⬅️</span>
                <span>Previous</span>
              </button>

              <div className="flex items-center space-x-2">
                <span className="text-slate-600 font-semibold">Page</span>
                <span className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-2 rounded-xl font-bold shadow-md">
                  {currentPage} of {totalPages}
                </span>
              </div>

              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-5 py-3 rounded-xl font-bold transition-all duration-200 flex items-center space-x-2 ${
                  currentPage === totalPages
                    ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                }`}
              >
                <span>Next</span>
                <span className="text-lg">➡️</span>
              </button>
            </div>

            <div className="text-sm text-slate-500 font-medium">
              Showing {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, filteredParts.length)} of {filteredParts.length} records
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------
   🔹 EDIT PARAMETERS COMPONENT
---------------------------- */
function EditParameters({ partno, itemid, onBack }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchExistingParams = async () => {
      try {
        setLoading(true);
        const databaseKey = sessionStorage.getItem("databaseKey");
        const userName = sessionStorage.getItem("userName");

        const response = await api.EditInspectionParam_API(
          itemid,
          userName,
          databaseKey
        );

        const data =
          response?.data?.EditInspectionParam_API_APIjs?.Table ||
          response?.EditInspectionParam_API_APIjs?.Table ||
          [];

        setRows(
          data.length > 0
            ? data
            : [
                {
                  Slno: 1,
                  Parameter: "",
                  Specification: "",
                  InsMethod: "",
                  Instrument: "",
                  MaxVal: "",
                  Minval: "",
                  Showrange: "",
                  Defaultval: "",
                  InwardInsp: false,
                  InProcInsp: false,
                  FinalInsp: false,
                  attachments: [],
                },
              ]
        );
      } catch (error) {
        console.error("❌ Error fetching inspection parameters:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchExistingParams();
  }, [itemid]);

  const handleChange = (index, field, value) => {
    const updated = [...rows];
    updated[index][field] = value;
    setRows(updated);
  };

  const handleCheckboxChange = (index, field) => {
    const updated = [...rows];
    updated[index][field] = !updated[index][field];
    setRows(updated);
  };

  const addLine = () => {
    setRows([
      ...rows,
      {
        Slno: rows.length + 1,
        Parameter: "",
        Specification: "",
        InsMethod: "",
        Instrument: "",
        MaxVal: "",
        Minval: "",
        Showrange: "",
        Defaultval: "",
        InwardInsp: false,
        InProcInsp: false,
        FinalInsp: false,
        attachments: [],
      },
    ]);
  };

  const deleteRow = (index) => {
    const updated = rows.filter((_, i) => i !== index);
    setRows(updated);
  };

  const saveData = async () => {
    try {
      const databaseKey = sessionStorage.getItem("databaseKey");
      const userName = sessionStorage.getItem("userName");

      const payload = {
        Itemid: itemid,
        Partno: partno,
        ItemTbl: rows,
      };

      console.log("📤 Sending Payload:", payload);

      const response = await api.SaveItemQcInspectionParam_API(
        payload,
        databaseKey,
        userName
      );

      console.log("✅ Server Response:", response.data);
      alert("✅ QC Parameters Saved Successfully!");
      setTimeout(() => navigate("/Master"), 800);
    } catch (error) {
      console.error("❌ Error saving QC Params:", error.response || error);
      alert("Error saving data! Check console for details.");
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-blue-600 text-lg font-medium animate-pulse">
          ⏳ Loading inspection parameters...
        </p>
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto bg-white rounded-xl shadow-md border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <button
            onClick={onBack}
            className="flex items-center gap-1 px-3 py-1 rounded bg-red-600 hover:bg-red-700 text-white"
          >
            Back
          </button>
          <h1 className="text-xl font-semibold text-gray-800">
            ✏️ Edit Parameters for{" "}
            <span className="text-blue-600">{partno}</span>
            <span className="text-gray-500 ml-2 text-base">
              (Item ID: <span className="text-blue-500">{itemid}</span>)
            </span>
          </h1>
          <div className="flex gap-2">
            <button
              onClick={saveData}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-1 rounded"
            >
              💾 Save
            </button>
            <button
              onClick={addLine}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1 rounded"
            >
              ➕ Add Row
            </button>
          </div>
        </div>

        <div className="overflow-x-auto border rounded-lg custom-scrollbar max-h-[70vh]">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                {[
                  "Delete",
                  "Slno",
                  "Parameter",
                  "Specification",
                  "InsMethod",
                  "Instrument",
                  "MaxVal",
                  "Minval",
                  "Showrange",
                  "Defaultval",
                  "InwardInsp",
                  "InProcInsp",
                  "FinalInsp",
                  "Attachments",
                ].map((head, i) => (
                  <th
                    key={i}
                    className="px-2 py-1 text-left font-medium text-gray-700"
                  >
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row, i) => (
                <tr key={i} className="hover:bg-blue-50 transition-colors">
                  <td className="px-2 py-1 text-center">
                    <button
                      onClick={() => deleteRow(i)}
                      className="bg-red-500 hover:bg-red-600 text-white text-xs px-3 py-1 rounded"
                    >
                      Delete
                    </button>
                  </td>
                  <td className="px-2 py-1 text-center">{row.Slno}</td>

                  {[
                    "Parameter",
                    "Specification",
                    "InsMethod",
                    "Instrument",
                    "MaxVal",
                    "Minval",
                    "Showrange",
                    "Defaultval",
                  ].map((field) => (
                    <td key={field} className="px-2 py-1">
                      <input
                        type={
                          ["MaxVal", "Minval", "Showrange", "Defaultval"].includes(
                            field
                          )
                            ? "number"
                            : "text"
                        }
                        value={row[field] || ""}
                        onChange={(e) =>
                          handleChange(i, field, e.target.value)
                        }
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 no-spinner"
                      />
                    </td>
                  ))}

                  {["InwardInsp", "InProcInsp", "FinalInsp"].map((field) => (
                    <td key={field} className="px-2 py-1 text-center">
                      <input
                        type="checkbox"
                        checked={row[field] || false}
                        onChange={() => handleCheckboxChange(i, field)}
                        className="h-4 w-4"
                      />
                    </td>
                  ))}

                  <td className="px-2 py-2 w-[80px]">
                    <Attachments
                      mdlId="Item_Master"
                      primeId={itemid}
                      primeName={partno}
                      uploadedBy={
                        localStorage.getItem("username") || "UnknownUser"
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------
   🔹 MAIN APP COMPONENT
---------------------------- */
export default function App() {
  const [editingPart, setEditingPart] = useState(null);

  return (
    <>
      {editingPart ? (
        <EditParameters
          partno={editingPart.PartNo}
          itemid={editingPart.Itemid}
          onBack={() => setEditingPart(null)}
        />
      ) : (
        <PartList onEdit={(part) => setEditingPart(part)} />
      )}
    </>
  );
}

/* Tailwind custom scrollbar + spinner removal */
const style = document.createElement("style");
style.innerHTML = `
  .no-spinner::-webkit-outer-spin-button,
  .no-spinner::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  .no-spinner {
    -moz-appearance: textfield;
  }
  .custom-scrollbar::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 10px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;
document.head.appendChild(style);
